import {
  FleetViewDevice,
  FleetViewSort,
  LiveRiskDecision,
  LiveRiskRead,
} from '../state/fleetView/types';

export type ExcursionState = 'open' | 'closed' | 'none';

export const DECISION_LABEL: Record<LiveRiskDecision, string> = {
  ACT: 'Act',
  WATCH: 'Watch',
  IGNORE: 'On track',
};

export const formatHours = (hours: number): string =>
  hours < 1 ? `${Math.round(hours * 60)}m` : `${Math.round(hours * 10) / 10}h`;

export const formatTimeLeft = (hours: number): string =>
  hours === 0 ? 'No time left' : `~${formatHours(hours)} left`;

export const liveRiskRead = (
  device: FleetViewDevice
): LiveRiskRead | undefined => {
  const risk = device.liveRisk;
  return risk && risk.decision !== null ? risk : undefined;
};

export const isLiveRiskUnavailable = (device: FleetViewDevice): boolean =>
  device.liveRisk?.decision === null;

export const liveRiskChipText = ({
  decision,
  hoursLeft,
}: Pick<LiveRiskRead, 'decision' | 'hoursLeft'>): string =>
  decision === 'IGNORE' || hoursLeft === null
    ? DECISION_LABEL[decision]
    : `${DECISION_LABEL[decision]} · ${formatTimeLeft(hoursLeft)}`;

export const excursionState = (
  excursions: FleetViewDevice['temperatureExcursions']
): ExcursionState => {
  if (excursions.some((excursion) => !excursion.eventEnd)) {
    return 'open';
  }
  return excursions.length > 0 ? 'closed' : 'none';
};

type DeviceComparator = (a: FleetViewDevice, b: FleetViewDevice) => number;

const DECISION_RANK: Record<LiveRiskDecision, number> = {
  ACT: 0,
  WATCH: 1,
  IGNORE: 2,
};

const UNSCORED_RANK = 3;

const updatedAt = (device: FleetViewDevice): number =>
  device.lastKnownLocation
    ? new Date(device.lastKnownLocation.timestamp).getTime()
    : 0;

const deployedAt = (device: FleetViewDevice): number =>
  device.journey?.startTime ? new Date(device.journey.startTime).getTime() : 0;

const attentionRank = (device: FleetViewDevice): number => {
  const risk = liveRiskRead(device);
  return risk ? DECISION_RANK[risk.decision] : UNSCORED_RANK;
};

const compareHoursLeft = (a: number | null, b: number | null): number => {
  if (a === b) {
    return 0;
  }
  if (a === null) {
    return 1;
  }
  if (b === null) {
    return -1;
  }
  return a - b;
};

const byUpdatedDesc: DeviceComparator = (a, b) => updatedAt(b) - updatedAt(a);

const byAttention: DeviceComparator = (a, b) =>
  attentionRank(a) - attentionRank(b) ||
  compareHoursLeft(
    liveRiskRead(a)?.hoursLeft ?? null,
    liveRiskRead(b)?.hoursLeft ?? null
  ) ||
  byUpdatedDesc(a, b);

const COMPARATORS: Record<FleetViewSort, DeviceComparator> = {
  [FleetViewSort.ATTENTION_FIRST]: byAttention,
  [FleetViewSort.UPDATED_DESC]: byUpdatedDesc,
  [FleetViewSort.DEPLOYED_ASC]: (a, b) => deployedAt(a) - deployedAt(b),
  [FleetViewSort.DEPLOYED_DESC]: (a, b) => deployedAt(b) - deployedAt(a),
};

export const compareFleetDevices = (sort: FleetViewSort): DeviceComparator =>
  COMPARATORS[sort];

export const hasLiveRisk = (devices: FleetViewDevice[]): boolean =>
  devices.some((device) => liveRiskRead(device) !== undefined);

export const sortOptions = (hasRisk: boolean): FleetViewSort[] =>
  Object.values(FleetViewSort).filter(
    (sort) => hasRisk || sort !== FleetViewSort.ATTENTION_FIRST
  );

export const effectiveSort = (
  chosen: FleetViewSort | null,
  devices: FleetViewDevice[]
): FleetViewSort => {
  const hasRisk = hasLiveRisk(devices);
  if (chosen !== null && sortOptions(hasRisk).includes(chosen)) {
    return chosen;
  }
  return hasRisk ? FleetViewSort.ATTENTION_FIRST : FleetViewSort.UPDATED_DESC;
};

export const matchesDecision = (
  device: FleetViewDevice,
  selected: LiveRiskDecision | null
): boolean => selected === null || device.liveRisk?.decision === selected;

export const decisionItems = (
  devices: FleetViewDevice[]
): { decision: LiveRiskDecision | undefined }[] =>
  devices.map((device) => ({ decision: liveRiskRead(device)?.decision }));

export const worstDecision = (
  decisions: (LiveRiskDecision | null | undefined)[]
): LiveRiskDecision | undefined =>
  decisions.reduce<LiveRiskDecision | undefined>(
    (worst, decision) =>
      decision &&
      (worst === undefined || DECISION_RANK[decision] < DECISION_RANK[worst])
        ? decision
        : worst,
    undefined
  );
